import axios from 'axios';
import { API_URL } from '../config';

// Helper function for error handling
const handleError = (error) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error("Error response:", error.response.data);
    throw error;
  } else if (error.request) {
    // The request was made but no response was received
    console.error("Error request:", error.request);
    throw new Error('No response received from server');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error("Error:", error.message);
    throw error;
  }
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (!token) {
    return {};
  }
  return {
    Authorization: `Bearer ${token}`
  };
};

export const inventoryService = {
  // Get inventory items with optional pagination, filtering, and search
  getInventory: async (page = 1, limit = 10, customerId = null, search = '') => {
    try {
      let url = `${API_URL}/admin/inventory?page=${page}&limit=${limit}`;
      
      if (customerId) {
        url += `&companyId=${customerId}`;
      }
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await axios.get(url, {
        headers: getAuthHeaders() 
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  
  // Get a specific inventory item by ID
  getInventoryItem: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/admin/inventory/${id}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  
  // Create a new inventory item
  createInventoryItem: async (itemData) => {
    try {
      const response = await axios.post(`${API_URL}/admin/inventory`, itemData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  
  // Update an existing inventory item
  updateInventoryItem: async (id, itemData) => {
    try {
      const response = await axios.put(`${API_URL}/admin/inventory/${id}`, itemData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  
  // Delete an inventory item
  deleteInventoryItem: async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/admin/inventory/${id}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  
  // Get all customers (for filters and dropdown selections)
  getCustomers: async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Search customers by name
  searchCustomers: async (searchTerm) => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers/search?q=${encodeURIComponent(searchTerm)}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  
  // For backwards compatibility, we'll keep this method but have it call the real API
  getMockInventoryItems: async (params) => {
    return inventoryService.getInventory(params);
  }
}; 
